<script setup lang="ts">
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useDeviceStore } from '@/stores/device'

const { t } = useI18n()
const deviceStore = useDeviceStore()

// 档位位置映射（百分比位置）- 1: 0%, 2: 20%, 3: 40%, 4: 60%, 5: 80%, 6: 100%
const positions: Record<number, number> = {
  1: 0.0,
  2: 0.2,
  3: 0.4,
  4: 0.6,
  5: 0.8,
  6: 1.0,
}

const minLevel = 1
const maxLevel = 6
const totalLevels = 6

const isDragging = ref(false)
const trackRef = ref<HTMLElement | null>(null)

const noiseTitle = computed(() => {
  return deviceStore.isMicroPhone ? 'ENC' : 'AI ENC'
})

const toggleNoise = (val: boolean) => {
  deviceStore.setNoiseEnabled(val)
}

// 设置档位值
const setLevel = (level: number) => {
  const clamped = Math.min(Math.max(level, minLevel), maxLevel)
  deviceStore.setNoiseLevel(clamped)
}

// 按钮调节
const updateLevel = (delta: number) => {
  setLevel(deviceStore.noiseLevel + delta)
}

// 旧版允许在关闭状态点击轨道/刻度：先开启降噪，再应用所选档位。
const selectLevel = async (value: number) => {
  const clamped = Math.min(Math.max(value, minLevel), maxLevel)
  if (!deviceStore.noiseEnabled) {
    await deviceStore.setNoiseEnabled(true, clamped)
    return
  }
  await deviceStore.setNoiseLevel(clamped)
}

// 点击刻度直接跳转
const onTickClick = (value: number) => {
  void selectLevel(value)
}

// 根据位置计算最近的档位
const getClosestValueFromPosition = (pos: number): number => {
  let closestValue = 1
  let minDistance = Infinity

  for (let [value, valuePos] of Object.entries(positions)) {
    const distance = Math.abs(pos - valuePos)
    if (distance < minDistance) {
      minDistance = distance
      closestValue = parseInt(value)
    }
  }

  return closestValue
}

// 开始拖动
const onThumbMouseDown = (e: MouseEvent) => {
  if (!deviceStore.noiseEnabled) return
  e.preventDefault()
  isDragging.value = true
  document.body.style.cursor = 'grabbing'
}

// 拖动过程中
const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !deviceStore.noiseEnabled || !trackRef.value) return

  const trackRect = trackRef.value.getBoundingClientRect()
  const pos = (e.clientX - trackRect.left) / trackRect.width

  const closestValue = getClosestValueFromPosition(pos)

  if (deviceStore.noiseLevel !== closestValue) {
    deviceStore.setNoiseLevel(closestValue)
  }
}

// 结束拖动
const onMouseUp = () => {
  if (isDragging.value) {
    isDragging.value = false
    document.body.style.cursor = ''
  }
}

// 点击轨道跳转
const onTrackClick = (e: MouseEvent) => {
  if (!trackRef.value) return

  const trackRect = trackRef.value.getBoundingClientRect()
  const pos = (e.clientX - trackRect.left) / trackRef.value.clientWidth

  const closestValue = getClosestValueFromPosition(pos)
  void selectLevel(closestValue)
}

// 计算进度条宽度百分比
const progressWidth = computed(() => {
  const pos = positions[deviceStore.noiseLevel] || 0
  return pos * 100 + '%'
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

const closePopup = () => {
  emit('close')
}

// 全局鼠标事件
onMounted(() => {
  document.addEventListener('mousemove', onMouseMove)
  document.addEventListener('mouseup', onMouseUp)
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onMouseMove)
  document.removeEventListener('mouseup', onMouseUp)
})
</script>

<template>
  <div class="noise-popup">
    <!-- 标题栏 -->
    <div class="popup-header">
      <div class="header-left">
        <span class="noise-title">{{ noiseTitle }}</span>
        <el-switch 
          v-model="deviceStore.noiseEnabled" 
          @change="toggleNoise"
          class="custom-switch"
        />
      </div>
      <div class="close-btn" @click="closePopup">
        <img src="/img/nav/nav_popup_closebtn.png" alt="close">
      </div>
    </div>

    <!-- 分隔线 -->
    <div class="divider"></div>

    <!-- 内容区 -->
    <div class="popup-body" :class="{ disabled: !deviceStore.noiseEnabled }">
      <div class="section-label">{{ t('noise.parameterAdjustment') }}</div>
      
      <div class="slider-container">
        <button 
          class="slider-button" 
          id="decrement"
          @click="updateLevel(-1)" 
          :disabled="!deviceStore.noiseEnabled || deviceStore.noiseLevel <= minLevel"
        >−</button>

        <div class="slider-wrapper">
          <div 
            ref="trackRef"
            class="slider-track" 
            @click="onTrackClick"
          >
            <div class="slider-progress" :style="{ width: progressWidth }"></div>
            <div class="slider-ticks">
              <span 
                v-for="i in totalLevels" 
                :key="i" 
                class="tick" 
                @click.stop="onTickClick(i)"
              ></span>
            </div>
            <div 
              class="slider-thumb" 
              :style="{ left: progressWidth }"
              :class="{ grabbing: isDragging }"
              @mousedown="onThumbMouseDown"
            ></div>
          </div>
        </div>

        <button 
          class="slider-button" 
          id="increment"
          @click="updateLevel(1)" 
          :disabled="!deviceStore.noiseEnabled || deviceStore.noiseLevel >= maxLevel"
        >+</button>
      </div>
    </div>
  </div>
</template>
<style scoped lang="scss">
.noise-popup {
  width: 450px;
  border: 1px solid rgba(47, 47, 55, 1);
  border-radius: 20px;
  background: rgba(27, 27, 27, 0.8);
  backdrop-filter: blur(40px);
  overflow: hidden;
  color: #fff;
  user-select: none;
}

.popup-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 20px;

  .header-left {
    display: flex;
    align-items: center;
    gap: 12px;

    .noise-title {
      font-size: 15px;
      font-weight: 500;
      color: rgba(255, 255, 255, 0.9);
    }
  }

  .close-btn {
    width: 22px;
    height: 22px;
    cursor: pointer;
    
    img {
      width: 100%;
      height: 100%;
    }

    &:hover {
      content: url('/img/nav/nav_popup_closebtn_hover.png');
    }
  }
}

.divider {
  width: 100%;
  height: 1px;
  background:rgba(255, 255, 255, 0.1);
}

.popup-body {
  padding: 14px 10px 20px;
  transition: opacity 0.3s;
  
  &.disabled {
    opacity: 0.4;

    // 轨道和刻度在关闭状态仍可点击，点击后会开启并应用目标档位。
    .slider-track {
      background: rgba(0, 0, 0, 0.7);
    }

    .slider-thumb {
      background: rgba(182, 182, 183, 1);
      border-color: rgba(182, 182, 183, 1);
      pointer-events: none;
    }
  }

  .section-label {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.7);
    margin: 0 0 20px 10px;
  }
}

.slider-container {
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 20px;
  background:rgba(15, 15, 15, 1);
  border-radius: 10px;

  .slider-button {
    width: 35px;
    height: 35px;
    border-radius: 50%;
    border: none;
    background: rgba(0, 0, 0, 0.4);
    color: rgba(255, 255, 255, 0.9);
    font-size: 30px;
    font-weight: 300;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s;
    flex-shrink: 0;
    line-height: 1;

    &:hover:not(:disabled) {
      background: rgba(255, 255, 255, 0.1);
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  #decrement,
  #increment {
    padding: 0;
    font-size: 30px;
    font-weight: 300;
    line-height: 1;
  }
}

.slider-wrapper {
  flex: 1;
  display: flex;
  align-items: center;
}

.slider-track {
  flex: 1;
  height: 15px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  position: relative;
  cursor: pointer;
  user-select: none;

  &[disabled] {
    background-color: #dcdcdc;
  }
}

.slider-progress {
  position: absolute;
  height: 100%;
  background-color: #376BED;
  border-radius: 8px;
  top: 0;
  left: 0;
  transition: width 0.15s ease-out;
}

.slider-ticks {
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 2px;
  pointer-events: none;

  .tick {
    width: 2.5px;
    height: 10px;
    background-color: rgba(255, 255, 255, 0.3);
    pointer-events: auto;
    cursor: pointer;
    align-self: center;

  }

  // 第一个和最后一个刻度隐藏
  :nth-child(1),
  :nth-last-child(1) {
    background-color: transparent;
    height: 2px;
  }
}

.slider-thumb {
  position: absolute;
  width: 20px;
  height: 20px;
  background: #376BED;
  border: 3px solid #fff;
  box-sizing: border-box;
  border-radius: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  transition: left 0.15s ease-out, transform 0.15s;
  box-shadow: none;
  z-index: 10;
  cursor: grab;
  user-select: none;

  &:active,
  &.grabbing {
    cursor: grabbing;
    transform: translate(-50%, -50%);
  }

  &[disabled] {
    background-color: #a9a9a9;
    border-color: #b0b0b0;
  }
}

// 深度修改 Element Plus Switch 样式
:deep(.custom-switch) {
  .el-switch__core {
    background-color: #2a2a2a !important;
    border: none;
    height: 27px;
    width: 50px !important;
    border-radius: 14px !important;
    
    .el-switch__action {
      background-color: #888;
      width: 20px;
      height: 20px;
      left: 5px !important;
    }
  }
  
  &.is-checked .el-switch__core {
    background-color: #4e75ff !important;
    
    .el-switch__action {
      background-color: #fff;
      left: calc(100% - 24px) !important;
    }
  }
}
</style>
